/**
 * Protocol version constants
 * Used to identify the protocol version of messages, for compatibility handling in future version upgrades
 * 
 * Version compatibility strategy:
 * - Only check minimum supported version, reject deprecated old versions
 * - Don't check maximum version, as new versions usually maintain backward compatibility with old message formats
 * - This way new version servers can be compatible with old version clients, without forcing client upgrades
 */
export var ProtocolVersion = {
  /** Current protocol version */
  CURRENT: 2,
  /** Minimum supported protocol version (messages below this version will be rejected) */
  MIN_SUPPORTED: 1
};

/**
 * Special origin values
 */
export var OriginConstant = {
  /** Wildcard origin (postMessage targetOrigin = '*') */
  ANY: '*'
};

/**
 * Auto-ack (ACK-only) limits.
 */
export var AutoAckConstant = {
  /** Max length of ack.id to be echoed back in auto-ack */
  MAX_ID_LENGTH: 1024,
  /** Max length of ack.meta to be echoed back in auto-ack */
  MAX_META_LENGTH: 5120
};

/**
 * Protocol version type
 */

/**
 * Protocol validation result
 */

/**
 * HTTP status code constants
 */
export var HttpStatus = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REQUEST_TIMEOUT: 408,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
};

/**
 * HTTP status text constants
 */
export var HttpStatusText = {
  [HttpStatus.OK]: 'OK',
  [HttpStatus.CREATED]: 'Created',
  [HttpStatus.NO_CONTENT]: 'No Content',
  [HttpStatus.BAD_REQUEST]: 'Bad Request',
  [HttpStatus.UNAUTHORIZED]: 'Unauthorized',
  [HttpStatus.FORBIDDEN]: 'Forbidden',
  [HttpStatus.NOT_FOUND]: 'Not Found',
  [HttpStatus.REQUEST_TIMEOUT]: 'Request Timeout',
  [HttpStatus.TOO_MANY_REQUESTS]: 'Too Many Requests',
  [HttpStatus.INTERNAL_SERVER_ERROR]: 'Internal Server Error',
  [HttpStatus.BAD_GATEWAY]: 'Bad Gateway',
  [HttpStatus.SERVICE_UNAVAILABLE]: 'Service Unavailable'
};

/**
 * Get status text
 */
export function getStatusText(code) {
  return HttpStatusText[code] || 'Unknown';
}

/**
 * Error code constants
 */
export var ErrorCode = {
  /** ACK confirmation timeout */
  ACK_TIMEOUT: 'ACK_TIMEOUT',
  /** Request timeout (synchronous) */
  TIMEOUT: 'TIMEOUT',
  /** Async request timeout */
  ASYNC_TIMEOUT: 'ASYNC_TIMEOUT',
  /** Request error */
  REQUEST_ERROR: 'REQUEST_ERROR',
  /** Method not found */
  METHOD_NOT_FOUND: 'METHOD_NOT_FOUND',
  /** No response */
  NO_RESPONSE: 'NO_RESPONSE',
  /** Protocol version not supported */
  PROTOCOL_UNSUPPORTED: 'PROTOCOL_UNSUPPORTED',
  /** iframe not ready */
  IFRAME_NOT_READY: 'IFRAME_NOT_READY',
  /** Stream error */
  STREAM_ERROR: 'STREAM_ERROR',
  /** Stream cancelled */
  STREAM_CANCELLED: 'STREAM_CANCELLED',
  /** Stream not bound */
  STREAM_NOT_BOUND: 'STREAM_NOT_BOUND',
  /** Target window closed */
  TARGET_WINDOW_CLOSED: 'TARGET_WINDOW_CLOSED',
  /** Too many concurrent requests (rate limiting) */
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  /** Stream start not received in time */
  STREAM_START_TIMEOUT: 'STREAM_START_TIMEOUT',
  /** Stream has already started */
  STREAM_ALREADY_STARTED: 'STREAM_ALREADY_STARTED',
  /** Stream has ended */
  STREAM_ENDED: 'STREAM_ENDED',
  /** Stream idle timeout */
  STREAM_TIMEOUT: 'STREAM_TIMEOUT',
  /** Stream expired */
  STREAM_EXPIRED: 'STREAM_EXPIRED',
  /** Stream read error */
  STREAM_READ_ERROR: 'STREAM_READ_ERROR',
  /** Stream write() is only available when mode is push */
  STREAM_WRITE_ONLY_IN_PUSH_MODE: 'STREAM_WRITE_ONLY_IN_PUSH_MODE',
  /** Stream pending queue overflow */
  STREAM_PENDING_QUEUE_OVERFLOW: 'STREAM_PENDING_QUEUE_OVERFLOW',
  /** Stream pending bytes overflow */
  STREAM_PENDING_BYTES_OVERFLOW: 'STREAM_PENDING_BYTES_OVERFLOW'
};

/**
 * Message type constants
 */
export var MessageType = {
  /** Request message */
  REQUEST: 'request',
  /** Acknowledge request received */
  ACK: 'ack',
  /** Async task notification */
  ASYNC: 'async',
  /** Response message */
  RESPONSE: 'response',
  /** Error message */
  ERROR: 'error',
  /** Ping message (for connection detection) */
  PING: 'ping',
  /** Pong message (for connection detection) */
  PONG: 'pong',
  /** Stream start */
  STREAM_START: 'stream_start',
  /** Stream data chunk */
  STREAM_DATA: 'stream_data',
  /** Stream end */
  STREAM_END: 'stream_end',
  /** Stream error */
  STREAM_ERROR: 'stream_error',
  /** Stream cancel */
  STREAM_CANCEL: 'stream_cancel',
  /** Stream pull (receiver requests next chunks) */
  STREAM_PULL: 'stream_pull'
};
export var MessageRole = {
  /** Server role */
  SERVER: 'server',
  /** Client role */
  CLIENT: 'client'
};
/**
 * Default timeout configuration (milliseconds)
 */
export var DefaultTimeout = {
  /** 
   * ACK confirmation timeout: 1000ms (1s)
   * Used for requireAck confirmation (ACK-only).
   * Increased from 500ms to accommodate slower environments or busy browsers where postMessage
   * serialization/deserialization may take longer.
   */
  ACK: 1000,
  /** Request timeout: 5s */
  REQUEST: 5000,
  /** Async request timeout: 120s */
  ASYNC: 120000
};

/**
 * HTTP Header name constants
 */
export var HttpHeader = {
  /** Set-Cookie (server sets cookie) */
  SET_COOKIE: 'Set-Cookie',
  /** Content-Type */
  CONTENT_TYPE: 'Content-Type',
  /** Content-Disposition (for file downloads) */
  CONTENT_DISPOSITION: 'Content-Disposition',
  /** Authorization */
  AUTHORIZATION: 'Authorization',
  /** Cookie (cookies carried in request) */
  COOKIE: 'Cookie'
};

/**
 * HTTP Header name type
 */

/**
 * Message type union type
 */

/**
 * Error code union type
 */

/**
 * Stream type constants
 */
export var StreamType = {
  /** Normal data stream */
  DATA: 'data',
  /** File stream */
  FILE: 'file'
};

/**
 * Stream mode constants
 * - PULL: receiver pulls next chunks (backpressure)
 * - PUSH: producer pushes via write()
 */
export var StreamMode = {
  PULL: 'pull',
  PUSH: 'push'
};
/**
 * Stream internal message type constants (for stream internal message handling)
 * Note: These are MessageType.STREAM_* values with the stream_ prefix removed
 */
export var StreamInternalMessageType = {
  /** Data message */
  DATA: 'data',
  /** End message */
  END: 'end',
  /** Error message */
  ERROR: 'error',
  /** Cancel message */
  CANCEL: 'cancel',
  /** Pull message */
  PULL: 'pull'
};

/**
 * Stream internal message type value type
 */

/**
 * Stream type value type
 */

/**
 * Stream state constants
 */
export var StreamState = {
  /** Pending */
  PENDING: 'pending',
  /** Streaming */
  STREAMING: 'streaming',
  /** Ended */
  ENDED: 'ended',
  /** Error */
  ERROR: 'error',
  /** Cancelled */
  CANCELLED: 'cancelled'
};

/**
 * Stream state value type
 */

/**
 * Stream event name constants (for stream.on / observability)
 */
export var StreamEvent = {
  START: 'start',
  DATA: 'data',
  READ: 'read',
  WRITE: 'write',
  SEND: 'send',
  PULL: 'pull',
  ACK: 'ack',
  END: 'end',
  CANCEL: 'cancel',
  ERROR: 'error',
  TIMEOUT: 'timeout',
  EXPIRED: 'expired',
  STATE: 'state'
};
/**
 * Message constants (for multi-language support)
 */
export { Messages, formatMessage, setMessages, resetMessages, getMessages } from './messages';
export { WarnOnceKey, buildWarnOnceKey } from './warn-once';
export { LogLevel } from './log';
export { DebugEvent } from './debug';